import React, { useState, useRef, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc,
  doc,
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getAuth } from 'firebase/auth';
import { citiesService } from '../firebase/services';
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';

const VendorOnboardingForm = ({ onClose, onSuccess, editingVendor = null }) => {
  const auth = getAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [bypassDocuments, setBypassDocuments] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    businessName: editingVendor?.businessName || editingVendor?.name || '',
    firstName: editingVendor?.firstName || '',
    lastName: editingVendor?.lastName || '',
    contactPhone: editingVendor?.contactPhone || editingVendor?.phone || '',
    contactEmail: editingVendor?.contactEmail || '',
    fssaiLicenseNo: editingVendor?.fssaiLicenseNo || editingVendor?.licenseNumber || '',
    gstNo: editingVendor?.gstNo || '',
    panNo: editingVendor?.panNo || '',
    aadharNo: editingVendor?.aadharNo || '',
    companyLegalStructure: editingVendor?.companyLegalStructure || '',
    hasBusinessInsurance: editingVendor?.hasBusinessInsurance || '',
    deliveryMode: editingVendor?.deliveryMode || 'self',
    serviceHours: editingVendor?.serviceHours || {
      breakfast: false,
      lunch: false,
      dinner: false
    },
    addressLine1: editingVendor?.addressLine1 || editingVendor?.address || '',
    addressLine2: editingVendor?.addressLine2 || '',
    city: editingVendor?.city || '',
    state: editingVendor?.state || '',
    pincode: editingVendor?.pincode || '',
    cuisines: editingVendor?.cuisines || [],
    deliveryModes: editingVendor?.deliveryModes || ['Delivery'],
    rating: editingVendor?.rating || 0,
    locationsServed: editingVendor?.locationsServed || []
  });

  // Document uploads
  const [documents, setDocuments] = useState({
    fssaiLicense: null,
    gstCertificate: null,
    panCard: null,
    aadharCard: null,
    bankDetails: null,
    shopEstablishment: null,
    menuPricing: null,
    kitchenPhotos: [],
    mouAgreement: null
  });

  // Document URLs after upload
  const [documentUrls, setDocumentUrls] = useState(editingVendor?.onboardingDocs || {});

  // Cities and States data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  // File input refs
  const fileInputRefs = useRef({});

  // Required documents
  const requiredDocuments = [
    'fssaiLicense',
    'gstCertificate', 
    'panCard',
    'aadharCard',
    'bankDetails',
    'shopEstablishment',
    'menuPricing',
    'kitchenPhotos'
  ];

  // Required fields
  const requiredFields = [
    'businessName',
    'firstName',
    'lastName',
    'contactPhone',
    'contactEmail',
    'fssaiLicenseNo',
    'gstNo',
    'panNo',
    'aadharNo',
    'companyLegalStructure',
    'hasBusinessInsurance',
    'addressLine1',
    'state',
    'city'
  ];

  // Cuisine options
  const cuisineOptions = [
    'Indian', 'Chinese', 'Italian', 'Mexican', 'Thai', 'Japanese', 'Korean', 
    'Mediterranean', 'American', 'French', 'Middle Eastern', 'Vietnamese', 
    'Greek', 'Spanish', 'Lebanese', 'Turkish', 'Fusion', 'Vegetarian', 'Vegan'
  ];

  // Delivery mode options
  const deliveryModeOptions = [
    'Delivery', 'Pickup', 'Dine-in', 'Drive-through', 'Catering', 'Food Truck'
  ];

  // Company legal structure options
  const companyLegalStructureOptions = [
    'Private Limited Company',
    'Limited Liability Partnership (LLP)',
    'Sole Proprietorship',
    'One Person Company (OPC)',
    'Partnership Firm',
    'Public Limited Company',
    'Section 8 Company',
    'Not Registered Business'
  ];

  // Business insurance options
  const businessInsuranceOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
  ];

  // Load states and cities on component mount
  useEffect(() => {
    const loadStatesAndCities = async () => {
      try {
        const uniqueStates = await citiesService.getUniqueStates();
        setStates(uniqueStates);

        const allCities = await citiesService.getAllCities();
        setCities(allCities);
      } catch (error) {
        console.error('Error loading states and cities:', error);
      }
    };

    loadStatesAndCities();
  }, []);

  // Update available cities when state changes
  useEffect(() => {
    const updateAvailableCities = () => {
      if (formData.state) {
        const filteredCities = cities
          .filter(city => city.State === formData.state)
          .map(city => city.City)
          .sort();
        setAvailableCities(filteredCities);
      } else {
        setAvailableCities([]);
      }
    };

    updateAvailableCities();
  }, [formData.state, cities]);

  // Validation functions
  const isFieldValid = (fieldName) => {
    const value = formData[fieldName];
    if (!value) return false;
    
    switch (fieldName) {
      case 'contactEmail':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case 'contactPhone':
        return /^[6-9]\d{9}$/.test(value);
      case 'panNo':
        const cleanPan = value.replace(/\s/g, '').toUpperCase();
        return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanPan);
      case 'aadharNo':
        const cleanAadhar = value.replace(/[\s-]/g, '');
        return /^[0-9]{12}$/.test(cleanAadhar);
      case 'fssaiLicenseNo':
        return value.length >= 5;
      case 'gstNo':
        return value.length >= 10;
      case 'firstName':
      case 'lastName':
        return value.trim().length >= 2;
      case 'addressLine1':
        return value.trim().length >= 5;
      case 'state':
      case 'city':
        return value.trim().length >= 2;
      case 'pincode':
        return /^[0-9]{6}$/.test(value.replace(/\s/g, ''));
      case 'companyLegalStructure':
        return value.trim().length > 0;
      case 'hasBusinessInsurance':
        return value === 'yes' || value === 'no';
      default:
        return value.trim().length > 0;
    }
  };

  const isDocumentUploaded = (docName) => {
    if (docName === 'kitchenPhotos') {
      return documents[docName] && documents[docName].length > 0;
    }
    return documents[docName] !== null;
  };

  const isFormValid = () => {
    const fieldsValid = requiredFields.every(field => isFieldValid(field));
    const documentsValid = bypassDocuments || 
      requiredDocuments.every(doc => isDocumentUploaded(doc));
    const serviceHoursValid = Object.values(formData.serviceHours).some(hour => hour);
    const cuisinesValid = formData.cuisines.length > 0;
    const deliveryModesValid = formData.deliveryModes.length > 0;
    
    return fieldsValid && documentsValid && serviceHoursValid && cuisinesValid && deliveryModesValid;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle state change and clear city
  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      state: state,
      city: ''
    }));
  };

  // Handle service hours changes
  const handleServiceHoursChange = (hour) => {
    setFormData(prev => ({
      ...prev,
      serviceHours: {
        ...prev.serviceHours,
        [hour]: !prev.serviceHours[hour]
      }
    }));
  };

  // Handle cuisine selection
  const handleCuisineChange = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      cuisines: prev.cuisines.includes(cuisine)
        ? prev.cuisines.filter(c => c !== cuisine)
        : [...prev.cuisines, cuisine]
    }));
  };

  // Handle delivery mode selection
  const handleDeliveryModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      deliveryModes: prev.deliveryModes.includes(mode)
        ? prev.deliveryModes.filter(m => m !== mode)
        : [...prev.deliveryModes, mode]
    }));
  };

  // Helper function to clean form data for saving
  const getCleanedVendorData = async () => {
    if (!formData.businessName || !formData.firstName || !formData.lastName || !formData.contactPhone || !formData.contactEmail) {
      throw new Error('Critical vendor information is missing');
    }

    // Get current user from managers collection
    let currentUserInfo = 'admin'; // fallback
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.firebaseUid) {
        // Get manager details from managers collection
        const managerDoc = await getDoc(doc(db, 'managers', currentUser.firebaseUid));
        if (managerDoc.exists()) {
          const managerData = managerDoc.data();
          currentUserInfo = managerData.email || managerData.name || currentUser.firebaseUid;
        } else {
          currentUserInfo = currentUser.firebaseUid;
        }
      }
    } catch (error) {
      console.error('Error getting current user:', error);
      currentUserInfo = 'admin';
    }

    return {
      businessName: formData.businessName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      contactPerson: `${formData.firstName} ${formData.lastName}`.trim(),
      contactPhone: formData.contactPhone,
      contactEmail: formData.contactEmail,
      fssaiLicenseNo: formData.fssaiLicenseNo,
      gstNo: formData.gstNo,
      panNo: formData.panNo.replace(/\s/g, '').toUpperCase(),
      aadharNo: formData.aadharNo.replace(/[\s-]/g, ''),
      companyLegalStructure: formData.companyLegalStructure,
      hasBusinessInsurance: formData.hasBusinessInsurance,
      deliveryMode: formData.deliveryMode,
      serviceHours: formData.serviceHours,
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      address: formData.addressLine1,
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      name: formData.businessName,
      contactName: `${formData.firstName} ${formData.lastName}`.trim(),
      phone: formData.contactPhone,
      licenseNumber: formData.fssaiLicenseNo,
      deliveryModes: formData.deliveryModes,
      cuisines: formData.cuisines,
      rating: formData.rating,
      locationsServed: formData.locationsServed,
      onboardingDocs: documentUrls,
      updatedAt: serverTimestamp(),
      updatedBy: currentUserInfo
    };
  };

  // Handle file upload
  const handleFileUpload = async (docName, file) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [docName]: true }));

    try {
      const storageRef = ref(storage, `MealDeskVendors/temp/${docName}_${Date.now()}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setDocumentUrls(prev => ({
        ...prev,
        [docName]: downloadURL
      }));

      if (docName === 'kitchenPhotos') {
        setDocuments(prev => ({
          ...prev,
          [docName]: [...(prev[docName] || []), file]
        }));
      } else {
        setDocuments(prev => ({
          ...prev,
          [docName]: file
        }));
      }
    } catch (error) {
      console.error(`Error uploading ${docName}:`, error);
      alert(`Error uploading ${docName}. Please try again.`);
    } finally {
      setUploading(prev => ({ ...prev, [docName]: false }));
    }
  };

  // Handle file input change
  const handleFileChange = (docName, event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(docName, file);
    }
  };

  // Remove uploaded document
  const removeDocument = (docName) => {
    setDocuments(prev => ({
      ...prev,
      [docName]: docName === 'kitchenPhotos' ? [] : null
    }));
    setDocumentUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[docName];
      return newUrls;
    });
  };

  // Save vendor to Firestore
  const saveVendor = async () => {
    if (!isFormValid()) {
      alert('Please complete all required fields and upload required documents.');
      return;
    }

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const currentUser = auth.currentUser;

      let vendorData;
      try {
        vendorData = await getCleanedVendorData();
      } catch (error) {
        alert('Error preparing vendor data. Please check all required fields.');
        setLoading(false);
        return;
      }

      if (editingVendor) {
        await updateDoc(doc(db, 'MealDeskVendors', editingVendor.id), vendorData);
        alert('Vendor updated successfully!');
      } else {
        if (!vendorData.businessName || !vendorData.contactPerson || !vendorData.contactPhone || !vendorData.contactEmail) {
          alert('Cannot create vendor with empty critical fields. Please fill all required information.');
          setLoading(false);
          return;
        }

        vendorData.isApproved = false;
        vendorData.bypassDocuments = false;
        vendorData.createdAt = serverTimestamp();
        vendorData.createdBy = currentUser?.uid || 'admin';
        vendorData.status = 'pending';

        const docRef = await addDoc(collection(db, 'MealDeskVendors'), vendorData);

        if (bypassDocuments) {
          await updateDoc(doc(db, 'MealDeskVendors', docRef.id), {
            isApproved: true,
            bypassDocuments: true,
            approvedBy: currentUser?.uid || 'admin',
            approvedOn: serverTimestamp(),
            status: 'approved'
          });
        }
      }

      setLoading(false);
      onSuccess && onSuccess(vendorData);
      onClose && onClose();

    } catch (error) {
      console.error('Error saving vendor:', error);
      alert('Error saving vendor. Please try again.');
      setLoading(false);
    }
  };

  // Approve vendor
  const approveVendor = async () => {
    if (!isFormValid()) {
      alert('Please complete all required fields and upload required documents.');
      return;
    }

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const currentUser = auth.currentUser;

      let vendorData;
      try {
        const cleanedData = await getCleanedVendorData();
        vendorData = {
          ...cleanedData,
          isApproved: true,
          bypassDocuments: bypassDocuments,
          approvedBy: currentUser?.uid || 'admin',
          approvedOn: serverTimestamp(),
          status: 'approved'
        };
      } catch (error) {
        alert('Error preparing vendor data. Please check all required fields.');
        setLoading(false);
        return;
      }

      if (editingVendor) {
        await updateDoc(doc(db, 'MealDeskVendors', editingVendor.id), vendorData);
        alert('Vendor approved and updated successfully!');
      } else {
        if (!vendorData.businessName || !vendorData.contactPerson || !vendorData.contactPhone || !vendorData.contactEmail) {
          alert('Cannot create approved vendor with empty critical fields. Please fill all required information.');
          setLoading(false);
          return;
        }

        vendorData.createdAt = serverTimestamp();
        vendorData.createdBy = currentUser?.uid || 'admin';

        const docRef = await addDoc(collection(db, 'MealDeskVendors'), vendorData);
      }

      setLoading(false);
      onSuccess && onSuccess(vendorData);
      onClose && onClose();

    } catch (error) {
      console.error('Error approving vendor:', error);
      alert('Error approving vendor. Please try again.');
      setLoading(false);
    }
  };

  // Render form field
  const renderField = (fieldName, label, type = 'text', required = false) => {
    const getPlaceholder = () => {
      switch (fieldName) {
        case 'panNo':
          return 'Enter PAN (e.g., ABCDE1234F)';
        case 'aadharNo':
          return 'Enter Aadhar (12 digits)';
        case 'contactPhone':
          return 'Enter 10-digit mobile number';
        case 'contactEmail':
          return 'Enter email address';
        case 'gstNo':
          return 'Enter GST number (e.g., 22AAAAA0000A1Z5)';
        case 'firstName':
          return 'Enter first name';
        case 'lastName':
          return 'Enter last name';
        case 'addressLine1':
          return 'Enter address line 1';
        case 'addressLine2':
          return 'Enter address line 2 (optional)';
        case 'state':
          return 'Enter state';
        case 'city':
          return 'Enter city';
        case 'pincode':
          return 'Enter 6-digit pincode';
        case 'companyLegalStructure':
          return 'Select company legal structure';
        case 'hasBusinessInsurance':
          return 'Select business insurance status';
        default:
          return `Enter ${label.toLowerCase()}`;
      }
    };

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type={type}
          value={formData[fieldName]}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            required && !isFieldValid(fieldName) ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder={getPlaceholder()}
        />
        {required && !isFieldValid(fieldName) && (
          <p className="text-xs text-red-500 mt-1">
            {fieldName === 'panNo' && 'Please enter a valid PAN number (e.g., ABCDE1234F)'}
            {fieldName === 'aadharNo' && 'Please enter a valid 12-digit Aadhar number'}
            {fieldName === 'contactPhone' && 'Please enter a valid 10-digit mobile number'}
            {fieldName === 'contactEmail' && 'Please enter a valid email address'}
            {fieldName === 'gstNo' && 'Please enter a valid GST number'}
            {fieldName === 'firstName' && 'Please enter a valid first name (minimum 2 characters)'}
            {fieldName === 'lastName' && 'Please enter a valid last name (minimum 2 characters)'}
            {fieldName === 'addressLine1' && 'Please enter a valid address (minimum 5 characters)'}
            {fieldName === 'state' && 'Please enter a valid state (minimum 2 characters)'}
            {fieldName === 'city' && 'Please enter a valid city (minimum 2 characters)'}
            {fieldName === 'pincode' && 'Please enter a valid 6-digit pincode'}
            {fieldName === 'companyLegalStructure' && 'Please select a company legal structure'}
            {fieldName === 'hasBusinessInsurance' && 'Please select business insurance status'}
            {!['panNo', 'aadharNo', 'contactPhone', 'contactEmail', 'gstNo', 'firstName', 'lastName', 'addressLine1', 'state', 'city', 'pincode', 'companyLegalStructure', 'hasBusinessInsurance'].includes(fieldName) && 'This field is required'}
          </p>
        )}
      </div>
    );
  };

  // Render document upload
  const renderDocumentUpload = (docName, label, required = false) => {
    const isUploaded = isDocumentUploaded(docName);
    const isUploading = uploading[docName];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        
        {!isUploaded ? (
          <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              ref={el => fileInputRefs.current[docName] = el}
              onChange={(e) => handleFileChange(docName, e)}
              accept="image/*,.pdf,.doc,.docx"
              className="hidden"
            />
            <button
              onClick={() => fileInputRefs.current[docName]?.click()}
              disabled={isUploading}
              className="flex items-center justify-center w-full space-x-2 text-gray-600 hover:text-blue-600"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              ) : (
                <DocumentArrowUpIcon className="h-5 w-5" />
              )}
              <span>{isUploading ? 'Uploading...' : 'Click to upload'}</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center space-x-2">
              <CheckIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-800">
                {docName === 'kitchenPhotos' 
                  ? `${documents[docName].length} photo(s) uploaded`
                  : 'Document uploaded'
                }
              </span>
            </div>
            <button
              onClick={() => removeDocument(docName)}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
      
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-4xl">
          <div className="h-full flex flex-col bg-white shadow-xl">
            <form onSubmit={(e) => e.preventDefault()} className="h-full flex flex-col">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {editingVendor ? 'Edit Vendor' : 'Vendor Onboarding'}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {editingVendor ? 'Update vendor information and documents' : 'Complete vendor registration and approval'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Step {currentStep} of 3</span>
                    <div className="flex space-x-1">
                      {[1, 2, 3].map(step => (
                        <div
                          key={step}
                          className={`w-2 h-2 rounded-full ${
                            step <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="px-6 py-4">
                  {currentStep === 1 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Vendor Details</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderField('businessName', 'Business Name', 'text', true)}
                        {renderField('firstName', "Vendor Contact's First Name", 'text', true)}
                        {renderField('lastName', "Vendor Contact's Last Name", 'text', true)}
                        {renderField('contactPhone', "Vendor Contact's Phone", 'tel', true)}
                        {renderField('contactEmail', "Vendor Contact's Email", 'email', true)}
                        {renderField('fssaiLicenseNo', 'FSSAI License No.', 'text', true)}
                        {renderField('gstNo', 'Vendor GST No', 'text', true)}
                        {renderField('panNo', 'Vendor PAN No', 'text', true)}
                        {renderField('aadharNo', 'Vendor Aadhar No', 'text', true)}
                      </div>

                      {/* Company Legal Structure */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Company Legal Structure <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.companyLegalStructure}
                          onChange={(e) => handleFieldChange('companyLegalStructure', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            !formData.companyLegalStructure ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Company Legal Structure</option>
                          {companyLegalStructureOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        {!formData.companyLegalStructure && (
                          <p className="text-xs text-red-500 mt-1">Company legal structure is required</p>
                        )}
                      </div>

                      {/* Business Insurance */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Do you have valid Business Insurance? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.hasBusinessInsurance}
                          onChange={(e) => handleFieldChange('hasBusinessInsurance', e.target.value)}
                          className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            !formData.hasBusinessInsurance ? 'border-red-300' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select Business Insurance Status</option>
                          {businessInsuranceOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {!formData.hasBusinessInsurance && (
                          <p className="text-xs text-red-500 mt-1">Business insurance status is required</p>
                        )}
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cuisines <span className="text-red-500">*</span>
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          <div className="grid grid-cols-2 gap-2">
                            {cuisineOptions.map((cuisine) => (
                              <label key={cuisine} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.cuisines.includes(cuisine)}
                                  onChange={() => handleCuisineChange(cuisine)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">{cuisine}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Delivery Modes <span className="text-red-500">*</span>
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          <div className="grid grid-cols-2 gap-2">
                            {deliveryModeOptions.map((mode) => (
                              <label key={mode} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.deliveryModes.includes(mode)}
                                  onChange={() => handleDeliveryModeChange(mode)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">{mode}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Delivery Partner <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.deliveryMode}
                          onChange={(e) => handleFieldChange('deliveryMode', e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="self">Self Delivery</option>
                          <option value="kerblet">Kerblet Delivery</option>
                          <option value="thirdParty">3rd Party Delivery</option>
                        </select>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Hours <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-2">
                          {Object.entries(formData.serviceHours).map(([hour, checked]) => (
                            <label key={hour} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleServiceHoursChange(hour)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 capitalize">{hour}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Address</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Address Line 1 <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.addressLine1}
                              onChange={(e) => handleFieldChange('addressLine1', e.target.value)}
                              placeholder="Enter address line 1"
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              value={formData.addressLine2}
                              onChange={(e) => handleFieldChange('addressLine2', e.target.value)}
                              placeholder="Enter address line 2 (optional)"
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          
                          {/* State Dropdown */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.state}
                              onChange={(e) => handleStateChange(e.target.value)}
                              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                !formData.state ? 'border-red-300' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select State</option>
                              {states.map((state) => (
                                <option key={state} value={state}>
                                  {state}
                                </option>
                              ))}
                            </select>
                            {!formData.state && (
                              <p className="text-xs text-red-500 mt-1">State is required</p>
                            )}
                          </div>

                          {/* City Dropdown */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              City <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.city}
                              onChange={(e) => handleFieldChange('city', e.target.value)}
                              disabled={!formData.state}
                              className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                !formData.city ? 'border-red-300' : 'border-gray-300'
                              } ${!formData.state ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            >
                              <option value="">{formData.state ? 'Select City' : 'Select State First'}</option>
                              {availableCities.map((city) => (
                                <option key={city} value={city}>
                                  {city}
                                </option>
                              ))}
                            </select>
                            {!formData.city && formData.state && (
                              <p className="text-xs text-red-500 mt-1">City is required</p>
                            )}
                          </div>

                                                     {renderField('pincode', 'Pincode')}
                         </div>
                       </div>

                       {/* Current Google Rating */}
                       <div className="mt-6">
                         <label className="block text-sm font-medium text-gray-700 mb-1">
                           Current Google Rating (0-5)
                         </label>
                         <input
                           type="number"
                           min="0"
                           max="5"
                           step="0.1"
                           value={formData.rating}
                           onChange={(e) => handleFieldChange('rating', parseFloat(e.target.value) || 0)}
                           className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                           placeholder="Enter rating (0-5)"
                         />
                       </div>
                     </div>
                   )}

                  {currentStep === 2 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Document Upload</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {renderDocumentUpload('fssaiLicense', 'FSSAI License', true)}
                        {renderDocumentUpload('gstCertificate', 'GST Certificate', true)}
                        {renderDocumentUpload('panCard', 'PAN Card', true)}
                        {renderDocumentUpload('aadharCard', 'Aadhar Card (Owner)', true)}
                        {renderDocumentUpload('bankDetails', 'Cancelled Cheque / Bank Details', true)}
                        {renderDocumentUpload('shopEstablishment', 'Shop & Establishment Certificate', true)}
                        {renderDocumentUpload('menuPricing', 'Menu & Pricing Document', true)}
                        {renderDocumentUpload('kitchenPhotos', 'Kitchen Photos', true)}
                        {renderDocumentUpload('mouAgreement', 'MoU / Agreement')}
                      </div>

                      {/* Bypass option */}
                      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                        <div className="flex items-center space-x-3">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                          <div className="flex-1">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={bypassDocuments}
                                onChange={(e) => setBypassDocuments(e.target.checked)}
                                className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                              />
                              <span className="ml-2 text-sm font-medium text-yellow-800">
                                Bypass Document Requirements
                              </span>
                            </label>
                            <p className="text-xs text-yellow-700 mt-1">
                              Enable this to approve vendor without all required documents
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Review & Approval</h3>
                      
                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Validation Summary</h4>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Required Fields</span>
                            <div className="flex items-center space-x-2">
                              {requiredFields.every(field => isFieldValid(field)) ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-xs ${requiredFields.every(field => isFieldValid(field)) ? 'text-green-600' : 'text-red-600'}`}>
                                {requiredFields.filter(field => isFieldValid(field)).length}/{requiredFields.length}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Required Documents</span>
                            <div className="flex items-center space-x-2">
                              {(bypassDocuments || requiredDocuments.every(doc => isDocumentUploaded(doc))) ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-xs ${(bypassDocuments || requiredDocuments.every(doc => isDocumentUploaded(doc))) ? 'text-green-600' : 'text-red-600'}`}>
                                {bypassDocuments ? 'Bypassed' : `${requiredDocuments.filter(doc => isDocumentUploaded(doc)).length}/${requiredDocuments.length}`}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Service Hours</span>
                            <div className="flex items-center space-x-2">
                              {Object.values(formData.serviceHours).some(hour => hour) ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-xs ${Object.values(formData.serviceHours).some(hour => hour) ? 'text-green-600' : 'text-red-600'}`}>
                                {Object.values(formData.serviceHours).filter(hour => hour).length} selected
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Cuisines</span>
                            <div className="flex items-center space-x-2">
                              {formData.cuisines.length > 0 ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-xs ${formData.cuisines.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formData.cuisines.length} selected
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Delivery Modes</span>
                            <div className="flex items-center space-x-2">
                              {formData.deliveryModes.length > 0 ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <XMarkIcon className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-xs ${formData.deliveryModes.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formData.deliveryModes.length} selected
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                              Approval Information
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>• Vendor will be automatically approved upon submission</p>
                              <p>• All data will be stored in the MealDeskVendors collection</p>
                              <p>• Documents will be uploaded to Firebase Storage</p>
                              {bypassDocuments && (
                                <p className="font-medium">• Document requirements have been bypassed</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 flex justify-between">
                <div className="flex space-x-3">
                  {currentStep > 1 && (
                    <button
                      onClick={() => setCurrentStep(currentStep - 1)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Previous
                    </button>
                  )}
                  
                  {currentStep < 3 && (
                    <button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Next
                    </button>
                  )}
                </div>

                <div className="flex space-x-3">
                  {currentStep === 3 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!loading && isFormValid()) {
                            saveVendor();
                          }
                        }}
                        disabled={loading || !isFormValid()}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : (editingVendor ? 'Update Vendor' : 'Save as Draft')}
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!loading && isFormValid()) {
                            approveVendor();
                          }
                        }}
                        disabled={loading || !isFormValid()}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Approving...' : (editingVendor ? 'Approve & Update' : 'Approve Vendor')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorOnboardingForm; 